/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902968860 — PulseScreen */

import { useTranslation } from 'react-i18next';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { ContentBox, ErrorText, Input, InputWrapper, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { LoyaltyChipBadge, useLoyaltyStore } from '../../../loyalty';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { PaymentOrFeedback } from '../../components/PaymentOrFeedback/PaymentOrFeedback';
import { PriceWithPlanogram } from '../../components/PriceDisplay/PriceWithPlanogram/PriceWithPlanogram';
import { useTimerTimeout } from '../../hooks/useTimerTimeout';
import { useVolumeOptions } from '../../hooks/useVolumeOptions';
import { AVAILABLE_PULSE_TRIES, LIMITS } from '../../payment.constants';
import style from './PulseScreen.module.css';

interface AmountCardProps {
    onClick: () => void;
    children: React.ReactNode;
    isSelected: boolean;
    isLoyaltyOption?: boolean;
}

const AmountCard = ({ onClick, children, isSelected, isLoyaltyOption }: AmountCardProps) => {
    return (
        <div
            className={
                `${style.pulseCard}${isSelected ? ` ${style.selected}` : ''}` +
                (isLoyaltyOption ? ` ${style.loyaltyOption}` : '')
            }
            onClick={onClick}
        >
            {isLoyaltyOption && <LoyaltyChipBadge />}
            {children}
        </div>
    );
};

const PulseScreenBody = () => {
    const { t } = useTranslation();
    const { onTimerTimeoutHandler } = useOrderActions();
    useTimerTimeout({ screenName: 'pulse', onTimeout: () => onTimerTimeoutHandler('pulse') });

    const {
        volume: tries,
        customVolume: customTry,
        amount: amountInSubunits,
        originalAmount: originalAmountSubunits,
        isAmountSent,
        isPaymentButtonVisible,
        volumeError,
        availableVolumes,
        cheapestVolume,
        isCheapestSelected,
        selectVolumeOption,
        onCustomVolumeChange,
    } = useVolumeOptions(LIMITS.SNACK, AVAILABLE_PULSE_TRIES, undefined, {
        screenName: 'pulse',
    });

    const isPrizeAvailable = useLoyaltyStore((s) => s.isPrizeAvailable());

    usePageTracking('Pulse screen');

    return (
        <ScreenWrapper>
            <ContentBox>
                <div className={style.amountCardsBox}>
                    {availableVolumes.map((number) => (
                        <AmountCard
                            key={number}
                            onClick={() => selectVolumeOption(number)}
                            isSelected={tries === number}
                            isLoyaltyOption={
                                isPrizeAvailable && number === cheapestVolume
                            }
                        >
                            <span className={style.cardSpan}>
                                {number} {t('key.plays', { count: number })}
                            </span>
                        </AmountCard>
                    ))}
                </div>
                <ScreenTitle>{t('Choose the number of games')}</ScreenTitle>
                <InputWrapper>
                    <Input
                        type="number"
                        placeholder={t<string>('Quantity')}
                        value={customTry && customTry > 0 ? customTry : ''}
                        onChange={onCustomVolumeChange}
                    />
                    <ErrorText>{volumeError}</ErrorText>
                </InputWrapper>
                
                <PriceWithPlanogram
                    className={style.priceBox}
                    amountInSubunits={amountInSubunits}
                    originalAmountSubunits={originalAmountSubunits}
                />

                <PaymentOrFeedback
                    isVisible={isPaymentButtonVisible && isAmountSent}
                    isLoyaltyEligible={isCheapestSelected}
                    screenName="pulse"
                />

            </ContentBox>
        </ScreenWrapper>
    );
};

const PulseScreen = withOrderHydration(PulseScreenBody);

export { PulseScreen };
